import { useCallback } from 'react';

import { getWindow } from '@change-corgi/core/window';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';
import { useCurrentUser, useLoginState } from '#src/app/shared/hooks/session';

// FIXME: temporary — originally introduced in PR #7487
/**
 * Returns a callback that reports to the error tracker when a signature submission is missing
 * expected name/email fields. Investigating reported errors where some of the signature data is
 * unexpectedly missing: https://changeorg.airbrake.io/locate/0005f829-acb7-76ad-5478-965c88824388
 * See also src/app/index.tsx.
 */
export function useReportEmptyFields() {
	const {
		data: {
			petition: { id },
		},
	} = usePetitionDetailsPageData();
	const {
		errorReporter: { report },
	} = useUtilityContext();
	const loginState = useLoginState();
	const user = useCurrentUser();

	return useCallback(
		(values: FormValues) => {
			const fieldsToCheck =
				loginState === 'GUEST' ? (['firstName', 'lastName', 'email'] as const) : (['firstName', 'lastName'] as const);
			if (fieldsToCheck.some((name) => !values[name])) {
				void report({
					error: new Error('Unexpected empty fields in signature data'),
					params: {
						// whether a field is absent, using a hash for aggregations
						emptyFields: fieldsToCheck.reduce((acc, name) => ({ ...acc, [name]: !values[name] }), {}),
						// the value of the empty fields, to check if they're null, undefined, empty string, or simply absent
						emptyFieldsValues: fieldsToCheck
							.filter((name) => !values[name])
							.reduce(
								(acc, name) => ({
									...acc,
									[name]: (() => {
										if (!(name in values)) {
											return '<missing>';
										}
										const value = values[name];
										// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
										if (value === undefined || value === null) {
											// eslint-disable-next-line @typescript-eslint/no-explicit-any
											return `<${value as any}>`;
										}
										return value;
									})(),
								}),
								{},
							),
						petitionId: id,
						userId: user?.id,
						// will help us figure out if that happens only to non-guests
						loginState,
						// will help us figure out if that could be related to FB auto-login
						connectedToFacebook: user?.connectedToFacebook,
						// will help us figure out if that could be related to client-side navigation
						// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
						initialHref: (getWindow() as any).INITIAL_HREF,
						currentHref: getWindow().location.href,
					},
				});
			}
		},
		[report, loginState, id, user],
	);
}
